import { ResourceEnum } from "src/constants/constants.ts";
import { getExonnApi } from "src/exonn-api/exonn-api.tsx";
import {
  omitPathParameters,
  replacePathParameters,
} from "@exonn/js-sdk/src/utils.ts";

import { type ClientConfig } from "src/client-config/getClientConfig.ts";
import { OrderTabs } from "src/admin/modules/order/constants.ts";
import { type DataProvider } from "@exonn/base";
import type { DefaultDataProviderMeta } from "src/system/module.types.ts";

export const defaultDataProvider = (
  clientConfig: ClientConfig,
): DataProvider<DefaultDataProviderMeta> => ({
  getList: async (params) => {
    const { resource, page, sort, filter, signal, meta } = params;

    const {
      refreshId,
      withAggregation,
      include,
      locale,
      filter2,
      useSimplePagination = false,
      groupKeys,
    } = meta || {};

    const reqParams = {
      filter,
      filter2,
      sort,
      page,
      groupKeys,
      withAggregation,
      refreshId,
      useSimplePagination,
      include,
    };
    const reqConfig = {
      signal,
      headers: {
        "X-Locale": locale,
      },
    };

    if (resource === ResourceEnum.AccountMovement) {
      const api = getExonnApi();
      return (await api.bill.getList(reqParams, null, reqConfig)).data;
    }
    const api = getExonnApi();

    return (
      await api.client.get(
        getModuleBaseUrl({
          resource,
          clientConfig,
        }),
        {
          params: reqParams,
          ...reqConfig,
        },
      )
    ).data;
  },
  deleteOne: async (params) => {
    const { resource, id, data, meta } = params;
    const additionalHeaders = meta?.additionalHeaders || {};
    const reqParams = { id: id };

    const api = getExonnApi();
    const url = `${getModuleBaseUrl({
      resource,
      clientConfig,
    })}/{id}`;
    return (
      await api.client.delete(replacePathParameters(url, reqParams), {
        params: omitPathParameters(reqParams, url),
        data: data,
        headers: additionalHeaders,
      })
    ).data;
  },
  createOne: async (params): Promise<any> => {
    const { resource, data, meta } = params;

    const { locale, additionalHeaders } = meta || {};
    const reqConfig = {
      headers: {
        "X-locale": locale,
        ...additionalHeaders,
      },
    };

    const baseUrl = getModuleBaseUrl({
      resource,
      clientConfig,
    });
    const api = getExonnApi();
    const url = `${baseUrl}`;
    return (
      await api.client.post(replacePathParameters(url, null), data, {
        params: omitPathParameters(null, url),
        ...reqConfig,
      })
    ).data;
  },
  updateOne: async (params): Promise<any> => {
    const { resource, data, meta, id } = params;
    const { locale, additionalHeaders, tab } = meta || {};
    const reqParams = {
      id,
    };
    const reqConfig = {
      headers: {
        "X-locale": locale,
        ...additionalHeaders,
      },
    };

    const getUpdateOneUrl = ({
      resource,
      clientConfig,
      tab,
    }: {
      resource: string;
      clientConfig: ClientConfig;
      tab?: string;
    }) => {
      if (resource === ResourceEnum.Document && tab === "addresses") {
        return `${getModuleBaseUrl({
          resource,
          clientConfig,
        })}/{id}/address`;
      }
      if (resource === ResourceEnum.Document && tab === "texts") {
        return `${getModuleBaseUrl({
          resource,
          clientConfig,
        })}/{id}/text`;
      }
      if (resource === ResourceEnum.Document && tab === "recurringSchedules") {
        return `${getModuleBaseUrl({
          resource,
          clientConfig,
        })}/{id}/recurring-schedule`;
      }
      if (resource === ResourceEnum.Order && tab === OrderTabs.Address) {
        return `${getModuleBaseUrl({
          resource,
          clientConfig,
        })}/{id}/address`;
      }
      return `${getModuleBaseUrl({
        resource,
        clientConfig,
      })}/{id}`;
    };

    const getUpdateOneHttpMethod = ({
      resource,
      tab,
    }: {
      resource: string;
      tab?: string;
    }) => {
      if (resource === ResourceEnum.Document && tab === "addresses") {
        return "patch";
      }
      if (resource === ResourceEnum.Document && tab === "texts") {
        return "patch";
      }
      if (resource === ResourceEnum.Document && tab === "recurringSchedules") {
        return "put";
      }
      if (resource === ResourceEnum.Order && tab === OrderTabs.Address) {
        return "patch";
      }
      return "put";
    };

    const api = getExonnApi();
    const url = getUpdateOneUrl({
      resource,
      clientConfig,
      tab,
    });
    return (
      await api.client[
        getUpdateOneHttpMethod({
          resource,
          tab,
        })
      ](replacePathParameters(url, reqParams), data, {
        params: omitPathParameters(reqParams, url),
        ...reqConfig,
      })
    ).data;
  },
  getCreateFormData: async (params) => {
    const { resource, signal, meta } = params;
    const { locale, requestParams, pretend } = meta;
    const reqConfig = {
      signal,
      headers: {
        "X-Locale": locale,
      },
    };

    if (pretend) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({});
        }, 500);
      });
    }

    const api = getExonnApi();

    const url = `${getModuleBaseUrl({
      resource,
      clientConfig,
    })}/new-form`;
    return (
      await api.client.get(url, {
        params: requestParams,
        data: null,
        ...reqConfig,
      })
    ).data;
  },
  getOne: async (params) => {
    const { resource, id, signal, meta } = params;
    const { locale, include, type } = meta || {};
    const reqConfig = {
      signal,
      headers: {
        "X-Locale": locale,
      },
    };
    const reqParams = { id, include };

    const api = getExonnApi();
    let url = `${getModuleBaseUrl({
      resource,
      clientConfig,
    })}/{id}`;

    if (type === "board" && resource === ResourceEnum.Picklist) {
      url = `${getModuleBaseUrl({
        resource,
        clientConfig,
      })}/{id}/board`;
    }

    return (
      await api.client.get(replacePathParameters(url, reqParams), {
        params: omitPathParameters(reqParams, url),
        data: null,
        ...reqConfig,
      })
    ).data;
  },
  cloneToLocale: async ({ resource, id, to_locale, from_locale }) => {
    const api = getExonnApi();
    return api.system.resource.copyLocale(
      {},
      {
        resource: resource,
        from_locale: from_locale,
        to_locale: to_locale,
        id: id,
      },
    );
  },
});

const getModuleBaseUrl = ({
  resource,
  clientConfig,
}: {
  resource: string;
  clientConfig: ClientConfig;
}) => {
  const exceptions: Record<string, string> = {
    [ResourceEnum.RecurringDocument]: "/document",
    [ResourceEnum.StockOwner]: "/stock",
    [ResourceEnum.StockWarehouse]: "/stock",
    [ResourceEnum.StockWarehousePlace]: "/stock",
    [ResourceEnum.Process]: "/system/processes",
  };

  if (exceptions?.[resource]) {
    return exceptions[resource];
  }

  return `/${resource}`;
};
